import { BookOpen, FileSpreadsheet, Gauge, LayoutList, TrendingDown, UserRoundSearch, UsersRound } from 'lucide-react';
import { canView } from '@nest-recruit/shared';
import type { router as AppRouter } from '../../router';
import { registerCommands, type Command } from '../../shell/command-registry';

/** S27–S29's palette entries (06-qol §1): the workspace, the command center, RPT-03 to RPT-06, the metric dictionary. */
export function registerReportCommands(router: typeof AppRouter): () => void {
  const commands: Command[] = [
    {
      id: 'reports.workspace',
      label: 'My workspace',
      section: 'navigate',
      context: 'Dashboard · RPT-02 · g then d',
      permission: 'reports_own',
      canSee: (session) => canView(session, 'reports_own'),
      icon: LayoutList,
      keywords: ['home', 'dashboard', 'today', 'my work', 'morning'],
      run: () => void router.navigate({ to: '/workspace' }),
    },
    {
      id: 'reports.commandCenter',
      label: 'Command center',
      section: 'navigate',
      context: 'Reports · RPT-01',
      permission: 'reports_all_staff',
      canSee: (session) => canView(session, 'reports_all_staff'),
      icon: Gauge,
      keywords: ['funnel', 'time to fill', 'breach', 'sla', 'cost per hire', 'source', 'aging', 'forecast', 'headcount'],
      run: () => void router.navigate({ to: '/reports/command-center', search: {} }),
    },
    {
      id: 'reports.proficiency',
      label: 'Recruiter proficiency',
      section: 'navigate',
      context: 'Reports · RPT-03 · a profile, never a ranking',
      permission: 'reports_own',
      canSee: (session) => canView(session, 'reports_own') && !session.isHiringManagerSurface,
      icon: UserRoundSearch,
      keywords: ['recruiter lag', 'profile', 'performance', 'my numbers', 'time to fill', 'conversion'],
      run: () => void router.navigate({ to: '/reports/proficiency', search: {} }),
    },
    {
      id: 'reports.scoreboard',
      label: 'Department scoreboard',
      section: 'navigate',
      context: 'Reports · RPT-04 · approvals, decisions, panels',
      permission: 'reports_own',
      canSee: (session) => canView(session, 'reports_all_staff') || session.isHiringManagerSurface,
      icon: UsersRound,
      keywords: ['department', 'panelist', 'severity', 'decision latency', 'approval latency', 'no-show', 'override', 'waiver'],
      run: () => void router.navigate({ to: '/reports/scoreboard', search: {} }),
    },
    {
      id: 'reports.dropOff',
      label: 'Drop-off analysis',
      section: 'navigate',
      context: 'Reports · RPT-05 · and source effectiveness',
      permission: 'reports_own',
      canSee: (session) => canView(session, 'reports_own'),
      icon: TrendingDown,
      keywords: ['withdrawal', 'rejection reasons', 'why candidates leave', 'source', 'cost per hire'],
      run: () => void router.navigate({ to: '/reports/drop-off', search: {} }),
    },
    {
      id: 'reports.builder',
      label: 'Report builder',
      section: 'navigate',
      context: 'Reports · RPT-06 · export, save, schedule',
      permission: 'reports_own',
      canSee: (session) => canView(session, 'reports_own'),
      icon: FileSpreadsheet,
      keywords: ['export', 'excel', 'csv', 'xlsx', 'saved report', 'schedule', 'email me', 'offer analytics', 'time to fill trend'],
      run: () => void router.navigate({ to: '/reports/builder', search: {} }),
    },
    {
      id: 'reports.metrics',
      label: 'Metric definitions',
      section: 'navigate',
      context: 'Reports · the metric dictionary',
      permission: 'reports_own',
      canSee: (session) => canView(session, 'reports_own'),
      icon: BookOpen,
      keywords: ['definition', 'dictionary', 'how is it counted', 'metric'],
      run: () => void router.navigate({ to: '/reports/metrics' }),
    },
  ];
  return registerCommands(commands);
}
